/* eslint-disable import/prefer-default-export */
import 'core-js/stable';
import 'regenerator-runtime/runtime';

import React, { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import {
  Route, Navigate, Routes,
} from 'react-router-dom';

import {
  AppProvider,
  ErrorPage,
  PageWrap,
} from '@edx/frontend-platform/react';
import store from 'data/store';
import {
  APP_READY,
  APP_INIT_ERROR,
  initialize,
  subscribe,
  mergeConfig,
} from '@edx/frontend-platform';
import { initAnalytics, identifyUser } from '@blend-ed/blendxplatform';

import { configuration } from './config';

import messages from './i18n';

import App from './App';
import Dashboard from './pages/dashboard';
import Courses from './pages/Courses';
import Discover from './pages/discover';
import CourseDetail from './pages/discover/CourseDetail';
import ProgramDetail from './pages/discover/ProgramDetail';
import Library from './pages/library';
import Programs from './pages/programs';
import ProgramErrorBoundary from './pages/programs/ProgramErrorBoundary';
import Pathways from './pages/pathways';
import PathwayComponentView from './pages/pathways/ComponentViewPage';
import PathwayDiscoverDetail from './pages/discover/PathwayDetail';
import Progress from './pages/progress';
import DetailedActivityMap from './pages/progress/DetailedActivityMap';
import AllOngoingCourses from './pages/progress/AllOngoingCourses';
import Organizations from './pages/select-org';
import LiveSessions from './pages/live-sessions';
import LiveSessionDetail from './pages/live-sessions/LiveSessionDetail';
import About from './pages/about';
import Support from './pages/support';
import ViewTicket from './pages/support/ViewTicket';
import Notifications from './pages/notifications';
import Wishlist from './pages/wishlist';
import NoticesWrapper from './components/NoticesWrapper';
import FeatureGate from './components/FeatureGate';

subscribe(APP_READY, () => {
  initAnalytics();
  identifyUser();
  const root = createRoot(document.getElementById('root'));

  root.render(
    <StrictMode>
      <AppProvider store={store}>
        <NoticesWrapper>
          <Routes>
            <Route element={<PageWrap><App /></PageWrap>}>
              <Route index element={<Dashboard />} />
              <Route path="/courses" element={<Courses />} />
              <Route path="/discover" element={<Discover />} />
              <Route
                path="/discover/program/:programId"
                element={<FeatureGate flag="features_programs" defaultOn><ProgramDetail /></FeatureGate>}
              />
              <Route
                path="/discover/pathway/:pathwayId"
                element={<FeatureGate flag="features_learning_pathway"><PathwayDiscoverDetail /></FeatureGate>}
              />
              <Route path="/discover/:courseId" element={<CourseDetail />} />
              <Route path="/library" element={<Library />} />
              <Route
                path="/programs/:programId"
                element={(
                  <FeatureGate flag="features_programs" defaultOn>
                    <ProgramErrorBoundary><Programs /></ProgramErrorBoundary>
                  </FeatureGate>
                )}
              />
              <Route
                path="/pathways/:pathwayId"
                element={(
                  <FeatureGate flag="features_learning_pathway">
                    <ProgramErrorBoundary><Pathways /></ProgramErrorBoundary>
                  </FeatureGate>
                )}
              />
              <Route
                path="/pathways/:pathwayId/component/:componentId"
                element={(
                  <FeatureGate flag="features_learning_pathway">
                    <ProgramErrorBoundary><PathwayComponentView /></ProgramErrorBoundary>
                  </FeatureGate>
                )}
              />
              <Route path="/progress" element={<Progress />} />
              <Route path="/progress/courses" element={<AllOngoingCourses />} />
              <Route path="/progress/activity-map" element={<DetailedActivityMap />} />
              <Route path="/organizations" element={<Organizations />} />
              <Route
                path="/live-sessions"
                element={<FeatureGate flag="features_live_sessions"><LiveSessions /></FeatureGate>}
              />
              <Route
                path="/live-sessions/:sessionId"
                element={<FeatureGate flag="features_live_sessions"><LiveSessionDetail /></FeatureGate>}
              />
              <Route path="/about" element={<About />} />
              <Route path="/support" element={<Support />} />
              <Route path="/support/:ticketId" element={<ViewTicket />} />
              <Route path="/notifications" element={<Notifications />} />
              <Route path="/wishlist" element={<Wishlist />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </NoticesWrapper>
      </AppProvider>
    </StrictMode>,
  );
});

subscribe(APP_INIT_ERROR, (error) => {
  const root = createRoot(document.getElementById('root'));

  root.render(
    <StrictMode>
      <ErrorPage message={error.message} />
    </StrictMode>,
  );
});

export const appName = 'LearnerHomeAppConfig';

initialize({
  handlers: {
    config: () => {
      mergeConfig(configuration, appName);
    },
  },
  messages,
  requireAuthenticatedUser: true,
  /** Fetches GET {LMS_BASE_URL}/api/user/v1/accounts/{username} and merges into authenticatedUser (name, profileImage, etc.) */
  hydrateAuthenticatedUser: true,
});
